import React from 'react';
import {
  Box, Typography, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip,
} from '@mui/material';

// ── Utilities ──────────────────────────────────────────────────────────────────

export function tryParseJson(text) {
  if (!text) return null;
  let t = text.trim();

  // Strip markdown code fences (```json ... ``` or ``` ... ```)
  const fence = t.match(/^```(?:json|js|javascript)?\s*([\s\S]*?)\s*```$/i);
  if (fence) t = fence[1].trim();

  try { return JSON.parse(t); } catch {}

  // Extract first JSON object/array from mixed text
  const match = t.match(/(\{[\s\S]*\}|\[[\s\S]*\])/);
  if (match) { try { return JSON.parse(match[0]); } catch {} }

  // Lenient: single-key `{ "key": "value with unescaped newlines" }` pattern.
  // LLMs sometimes emit literal newlines inside JSON strings.
  if (t.startsWith('{') && t.trimEnd().endsWith('}')) {
    const firstQuote = t.indexOf('"');
    const secondQuote = t.indexOf('"', firstQuote + 1);
    const colonIdx = t.indexOf(':', secondQuote + 1);
    const valQuoteOpen = t.indexOf('"', colonIdx + 1);
    const valQuoteClose = t.lastIndexOf('"', t.trimEnd().length - 2);
    if (firstQuote >= 0 && colonIdx > 0 && valQuoteOpen > 0 && valQuoteClose > valQuoteOpen) {
      const key = t.substring(firstQuote + 1, secondQuote);
      const value = t.substring(valQuoteOpen + 1, valQuoteClose);
      if (key && value) return { [key]: value };
    }
  }

  return null;
}

export function deepParseStrings(value) {
  if (typeof value === 'string') {
    const inner = tryParseJson(value);
    return inner !== null ? deepParseStrings(inner) : value;
  }
  if (Array.isArray(value)) return value.map(deepParseStrings);
  if (value && typeof value === 'object')
    return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, deepParseStrings(v)]));
  return value;
}

export function isRowArray(val) {
  return Array.isArray(val) && val.length > 0 && val.every(v => v && typeof v === 'object' && !Array.isArray(v));
}

export function formatKey(key) {
  return String(key).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

// ── Generic JSON fallback ──────────────────────────────────────────────────────

export function RenderJsonValue({ data, depth = 1 }) {
  if (data === null || data === undefined) return null;
  if (typeof data === 'string')
    return <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8, mb: 0.5 }}>{data}</Typography>;
  if (typeof data === 'number' || typeof data === 'boolean')
    return <Typography variant="body2" sx={{ mb: 0.5 }}>{String(data)}</Typography>;
  if (Array.isArray(data))
    return (
      <Box>
        {data.map((item, i) => <Box key={i} sx={{ mb: 0.75 }}><RenderJsonValue data={item} depth={depth} /></Box>)}
      </Box>
    );
  if (typeof data === 'object')
    return (
      <Box>
        {Object.entries(data).map(([key, value]) => (
          <Box key={key} sx={{ mb: depth === 1 ? 1.5 : 0.75 }}>
            <Typography variant={depth === 1 ? 'subtitle2' : 'body2'} fontWeight={600} sx={{ mb: 0.25, textTransform: 'capitalize' }}>
              {formatKey(key)}
            </Typography>
            <Box sx={{ pl: depth > 1 ? 1.5 : 0 }}>
              <RenderJsonValue data={value} depth={depth + 1} />
            </Box>
          </Box>
        ))}
      </Box>
    );
  return <Typography variant="body2">{String(data)}</Typography>;
}

// ── Cell value renderer (handles nested objects/arrays in table cells) ────────

export function renderCellValue(val) {
  if (val == null) return '—';
  if (typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean') return String(val);
  if (Array.isArray(val)) {
    if (val.length === 0) return '—';
    return val.map(v => renderCellValue(v)).join(', ');
  }
  if (typeof val === 'object') {
    return Object.entries(val).map(([k, v]) => `${formatKey(k)}: ${renderCellValue(v)}`).join(' · ');
  }
  return String(val);
}

// ── Shared table header style ──────────────────────────────────────────────────

export const TABLE_HEAD_SX = {
  fontWeight: 700, fontSize: '0.72rem', color: 'text.secondary',
  textTransform: 'uppercase', letterSpacing: '0.05em', py: 1,
};

// ── Lab Report ─────────────────────────────────────────────────────────────────

export function getLabFlag(row) {
  const field = ['flag', 'status', 'result_flag', 'abnormal_flag', 'interpretation'].find(f => row[f] !== undefined);
  if (!field) return null;
  const raw = String(row[field] || '').trim().toUpperCase();
  if (!raw || ['NORMAL', 'WNL', 'WITHIN NORMAL LIMITS', 'N', 'NEG', 'NEGATIVE'].includes(raw)) return null;
  if (['HH', 'CRITICAL HIGH'].includes(raw)) return { label: 'HH', color: 'error' };
  if (['LL', 'CRITICAL LOW'].includes(raw)) return { label: 'LL', color: 'error' };
  if (['PANIC', 'CRITICAL', 'CRIT'].some(v => raw.startsWith(v))) return { label: '!!', color: 'error' };
  if (['H', 'HIGH', 'ABOVE', 'ELEVATED', 'INCREASED'].some(v => raw === v || raw.startsWith(v + ' '))) return { label: 'H', color: 'warning' };
  if (['L', 'LOW', 'BELOW', 'DECREASED'].some(v => raw === v || raw.startsWith(v + ' '))) return { label: 'L', color: 'info' };
  if (raw === 'ABNORMAL') return { label: 'ABN', color: 'warning' };
  return null;
}

export function LabTable({ rows }) {
  const allKeys = Array.from(new Set(rows.flatMap(r => Object.keys(r))));
  const valueKey = ['value', 'result', 'result_value', 'lab_value'].find(k => allKeys.includes(k));
  const flagKey = ['flag', 'result_flag', 'abnormal_flag', 'interpretation'].find(k => allKeys.includes(k));

  return (
    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5, overflow: 'hidden' }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: 'grey.50' }}>
            {allKeys.map(k => <TableCell key={k} sx={TABLE_HEAD_SX}>{formatKey(k)}</TableCell>)}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => {
            const flag = getLabFlag(row);
            const resultColor = !flag ? 'text.primary'
              : flag.color === 'error' ? 'error.main'
              : flag.color === 'warning' ? 'warning.dark'
              : 'info.dark';
            return (
              <TableRow key={i} sx={{ '&:last-child td': { border: 0 }, '&:hover': { bgcolor: 'action.hover' } }}>
                {allKeys.map(k => {
                  if (k === flagKey) {
                    return (
                      <TableCell key={k} sx={{ py: 0.75 }}>
                        {flag
                          ? <Chip label={flag.label} color={flag.color} size="small" sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700 }} />
                          : <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 600, fontSize: '0.75rem' }}>✓</Typography>
                        }
                      </TableCell>
                    );
                  }
                  if (k === valueKey) {
                    return (
                      <TableCell key={k} sx={{ fontFamily: 'monospace', fontSize: '0.875rem', fontWeight: flag ? 700 : 400, color: resultColor }}>
                        {row[k] ?? '—'}
                      </TableCell>
                    );
                  }
                  return (
                    <TableCell key={k} sx={{ fontSize: '0.85rem' }}>
                      {renderCellValue(row[k])}
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function renderLabPanels(panels) {
  const panelResultKey = ['results', 'tests', 'items', 'labs', 'lab_results'].find(
    k => panels[0] && isRowArray(panels[0][k])
  );
  if (!panelResultKey) return <LabTable rows={panels} />;
  return (
    <Box>
      {panels.map((panel, i) => {
        const panelNameKey = Object.keys(panel).find(k => k !== panelResultKey && typeof panel[k] === 'string' && !['date', 'time'].some(d => k.toLowerCase().includes(d)));
        const dateKey = Object.keys(panel).find(k => k.toLowerCase().includes('date'));
        const timeKey = Object.keys(panel).find(k => k.toLowerCase().includes('time'));
        const label = [
          panelNameKey ? panel[panelNameKey] : `Panel ${i + 1}`,
          dateKey ? panel[dateKey] : null,
          timeKey ? panel[timeKey] : null,
        ].filter(Boolean).join(' — ');
        return (
          <Box key={i} sx={{ mb: 3 }}>
            <Typography variant="overline" sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: '0.08em', display: 'block', mb: 0.75 }}>
              {label}
            </Typography>
            <LabTable rows={panel[panelResultKey]} />
          </Box>
        );
      })}
    </Box>
  );
}

export function LabSection({ data }) {
  if (isRowArray(data)) return renderLabPanels(data);
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    const entries = Object.entries(data);
    if (entries.length === 1 && isRowArray(entries[0][1])) return renderLabPanels(entries[0][1]);
    if (entries.some(([, v]) => isRowArray(v))) {
      return (
        <Box>
          {entries.map(([k, v]) => (
            <Box key={k} sx={{ mb: 3 }}>
              <Typography variant="overline" sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: '0.08em', display: 'block', mb: 0.75 }}>
                {formatKey(k)}
              </Typography>
              {isRowArray(v) ? renderLabPanels(v) : <RenderJsonValue data={v} depth={2} />}
            </Box>
          ))}
        </Box>
      );
    }
  }
  return <RenderJsonValue data={data} depth={1} />;
}

// ── Medications ────────────────────────────────────────────────────────────────

export function MedicationsTable({ rows }) {
  const allKeys = Array.from(new Set(rows.flatMap(r => Object.keys(r))));
  const nameKey = ['medication', 'name', 'drug', 'drug_name', 'medication_name'].find(k => allKeys.includes(k)) || allKeys[0];

  return (
    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5, overflow: 'hidden' }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: 'grey.50' }}>
            {allKeys.map(k => <TableCell key={k} sx={TABLE_HEAD_SX}>{formatKey(k)}</TableCell>)}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => (
            <TableRow key={i} sx={{ '&:last-child td': { border: 0 }, '&:hover': { bgcolor: 'action.hover' } }}>
              {allKeys.map(k => (
                <TableCell key={k} sx={{ fontSize: '0.85rem', fontWeight: k === nameKey ? 600 : 400 }}>
                  {renderCellValue(row[k])}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export function MedicationsSection({ data }) {
  if (isRowArray(data)) return <MedicationsTable rows={data} />;
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    const entries = Object.entries(data);
    if (entries.length === 1 && isRowArray(entries[0][1])) return <MedicationsTable rows={entries[0][1]} />;
    if (entries.some(([, v]) => isRowArray(v))) {
      return (
        <Box>
          {entries.map(([k, v]) => (
            <Box key={k} sx={{ mb: 2 }}>
              <Typography variant="overline" sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 0.75 }}>
                {formatKey(k)}
              </Typography>
              {isRowArray(v) ? <MedicationsTable rows={v} /> : <RenderJsonValue data={v} depth={2} />}
            </Box>
          ))}
        </Box>
      );
    }
  }
  return <RenderJsonValue data={data} depth={1} />;
}

// ── Physical Exam ──────────────────────────────────────────────────────────────

export const VITAL_DEFS = [
  { keys: ['heart_rate', 'hr', 'pulse', 'heart rate'], label: 'Heart Rate', unit: 'bpm' },
  { keys: ['blood_pressure', 'bp', 'blood pressure'], label: 'Blood Pressure', unit: 'mmHg' },
  { keys: ['temperature', 'temp', 'temperature_f', 'temperature_c', 'temp_f', 'temp_c'], label: 'Temp', unit: '°F' },
  { keys: ['oxygen_saturation', 'spo2', 'o2_sat', 'pulse_ox', 'o2 sat'], label: 'SpO₂', unit: '%' },
  { keys: ['respiratory_rate', 'rr', 'resp_rate', 'respiratory rate'], label: 'Resp Rate', unit: '/min' },
  { keys: ['weight', 'weight_kg', 'weight_lbs'], label: 'Weight', unit: '' },
  { keys: ['bmi', 'body mass index'], label: 'BMI', unit: '' },
  { keys: ['height', 'height_cm', 'height_in'], label: 'Height', unit: '' },
];

export function VitalsRow({ vitals }) {
  const found = VITAL_DEFS.map(def => {
    const matchKey = def.keys.find(k => vitals[k] !== undefined);
    return matchKey ? { ...def, value: vitals[matchKey] } : null;
  }).filter(Boolean);

  if (!found.length) return null;

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 0.5 }}>
      {found.map(v => (
        <Box key={v.label} sx={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.100',
          borderRadius: 2, px: 2, py: 1.5, minWidth: 90, textAlign: 'center',
        }}>
          <Typography sx={{ textTransform: 'uppercase', letterSpacing: '0.07em', fontSize: '0.62rem', fontWeight: 700, color: 'text.secondary', mb: 0.5 }}>
            {v.label}
          </Typography>
          <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', lineHeight: 1.2, color: 'primary.main' }}>
            {String(v.value)}
          </Typography>
          {v.unit && (
            <Typography sx={{ fontSize: '0.62rem', color: 'text.secondary', mt: 0.25 }}>{v.unit}</Typography>
          )}
        </Box>
      ))}
    </Box>
  );
}

export function PhysicalExamContent({ data }) {
  const vitalsKey = Object.keys(data).find(k => k.toLowerCase().replace(/[\s_]/g, '').includes('vital'));
  const vitalsObj = vitalsKey && typeof data[vitalsKey] === 'object' ? data[vitalsKey] : null;
  const systemsEntries = Object.entries(data).filter(([k]) => k !== vitalsKey);

  return (
    <Box>
      {vitalsObj && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="overline" sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: '0.08em', display: 'block', mb: 1 }}>
            Vital Signs
          </Typography>
          <VitalsRow vitals={vitalsObj} />
        </Box>
      )}
      {systemsEntries.length > 0 && (
        <Box>
          {vitalsObj && (
            <Typography variant="overline" sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: '0.08em', display: 'block', mb: 1 }}>
              Systems
            </Typography>
          )}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 1.25 }}>
            {systemsEntries.map(([k, v]) => (
              <Box key={k} sx={{ bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200', borderRadius: 1.5, p: 1.5 }}>
                <Typography sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary', letterSpacing: '0.05em', fontSize: '0.68rem', display: 'block', mb: 0.5 }}>
                  {formatKey(k)}
                </Typography>
                {typeof v === 'string'
                  ? <Typography variant="body2" sx={{ lineHeight: 1.65 }}>{v}</Typography>
                  : <RenderJsonValue data={v} depth={2} />
                }
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}

// ── Assessment & Plan ──────────────────────────────────────────────────────────

export function AssessmentAndPlan({ data }) {
  const problemsKey = ['problems', 'problem_list', 'diagnoses', 'conditions', 'issues', 'assessments'].find(k => isRowArray(data[k]));
  const problems = problemsKey ? data[problemsKey] : null;

  if (problems) {
    return (
      <Box>
        {problems.map((prob, i) => {
          const nameKey = ['problem', 'diagnosis', 'condition', 'name', 'title', 'assessment'].find(k => prob[k]);
          const descKey = ['description', 'detail', 'note', 'status', 'evaluation'].find(k => prob[k] && k !== nameKey);
          const planKey = ['plan', 'management', 'treatment', 'interventions', 'actions'].find(k => prob[k]);
          const planVal = prob[planKey];
          const planItems = Array.isArray(planVal) ? planVal : (planVal ? [planVal] : null);

          return (
            <Box key={i} sx={{ mb: 2.5, pl: 2, borderLeft: '3px solid', borderColor: 'primary.main' }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5, fontSize: '0.9rem' }}>
                {i + 1}. {nameKey ? prob[nameKey] : `Problem ${i + 1}`}
              </Typography>
              {descKey && prob[descKey] && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75, lineHeight: 1.65 }}>
                  {typeof prob[descKey] === 'string' ? prob[descKey] : JSON.stringify(prob[descKey])}
                </Typography>
              )}
              {planItems && (
                <Box component="ul" sx={{ m: 0, pl: 2.5, mt: 0.5 }}>
                  {planItems.map((item, j) => (
                    <Box component="li" key={j} sx={{ mb: 0.25 }}>
                      <Typography variant="body2" sx={{ lineHeight: 1.65 }}>
                        {typeof item === 'string' ? item : <RenderJsonValue data={item} depth={2} />}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    );
  }

  return <RenderJsonValue data={data} depth={1} />;
}

// ── Generic table for array-of-objects ────────────────────────────────────────

export function GenericDataTable({ rows }) {
  const allKeys = Array.from(new Set(rows.flatMap(r => Object.keys(r)))).slice(0, 8);
  return (
    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5, overflow: 'hidden' }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: 'grey.50' }}>
            {allKeys.map(k => <TableCell key={k} sx={TABLE_HEAD_SX}>{formatKey(k)}</TableCell>)}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => (
            <TableRow key={i} sx={{ '&:last-child td': { border: 0 }, '&:hover': { bgcolor: 'action.hover' } }}>
              {allKeys.map(k => (
                <TableCell key={k} sx={{ fontSize: '0.85rem' }}>
                  {renderCellValue(row[k])}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ── Section Renderer ──────────────────────────────────────────────────────────

export function SectionRenderer({ sectionKey, text, isStreaming }) {
  if (!text) {
    if (isStreaming) return (
      <Box display="flex" alignItems="center" gap={1}>
        <CircularProgress size={14} thickness={5} />
        <Typography variant="body2" color="text.secondary">Generating…</Typography>
      </Box>
    );
    return null;
  }

  const parsed = tryParseJson(text);
  if (parsed === null) {
    // Strip any markdown fences that survived JSON parsing attempts
    const displayText = text.replace(/^```(?:json|js|javascript)?\s*/i, '').replace(/\s*```\s*$/, '').trim();
    return (
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.85, fontSize: '0.9rem', color: 'text.primary' }}>
        {displayText}
      </Typography>
    );
  }

  const deep = deepParseStrings(parsed);

  // Single string wrapper — LLMs sometimes wrap content in one key
  if (deep && typeof deep === 'object' && !Array.isArray(deep)) {
    const keys = Object.keys(deep);
    if (keys.length === 1 && typeof deep[keys[0]] === 'string') {
      return (
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.85, fontSize: '0.9rem' }}>
          {deep[keys[0]]}
        </Typography>
      );
    }
  }

  if (sectionKey === 'lab_report') return <LabSection data={deep} />;
  if (sectionKey === 'medications') return <MedicationsSection data={deep} />;
  if (sectionKey === 'physical_exam' && deep && typeof deep === 'object' && !Array.isArray(deep))
    return <PhysicalExamContent data={deep} />;
  if (sectionKey === 'assessment_and_plan' && deep && typeof deep === 'object' && !Array.isArray(deep))
    return <AssessmentAndPlan data={deep} />;

  // Generic: top-level array of objects → table
  if (isRowArray(deep)) return <GenericDataTable rows={deep} />;

  // Generic: object where some values are arrays of objects → sectioned tables
  if (deep && typeof deep === 'object' && !Array.isArray(deep) && Object.values(deep).some(v => isRowArray(v))) {
    return (
      <Box>
        {Object.entries(deep).map(([k, v]) => (
          <Box key={k} sx={{ mb: 3 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1, textTransform: 'capitalize' }}>
              {formatKey(k)}
            </Typography>
            {isRowArray(v) ? <GenericDataTable rows={v} /> : <RenderJsonValue data={v} depth={2} />}
          </Box>
        ))}
      </Box>
    );
  }

  return <RenderJsonValue data={deep} depth={1} />;
}
